/**
 * Black Matte VTC - Partner Layout
 * Main layout for Partner Dashboard with navigation and credit status
 */

import React, { useState, useEffect } from 'react';
import Seo from '../Seo';
import { seo } from '../../seo/seoData';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { partnerService } from '../../services/partnerService';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  LayoutDashboard, 
  Calendar, 
  FileText, 
  CreditCard,
  LogOut,
  Menu,
  X,
  AlertTriangle,
  Building2,
  ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Navigation items
const navItems = [
  { href: '/partner/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/partner/bookings', label: 'Bookings', icon: Calendar },
  { href: '/partner/invoices', label: 'Invoices', icon: FileText },
  { href: '/partner/payments', label: 'Payments', icon: CreditCard },
];

// Credit Status Badge - Always visible
const CreditStatusBadge = ({ balance, creditLimit, hasOverdue }) => {
  const availableCredit = Math.max(0, creditLimit - balance);
  const usagePercent = creditLimit > 0 ? (balance / creditLimit) * 100 : 0;
  
  // Determine status
  let status = 'healthy';
  let statusColor = 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
  
  if (hasOverdue) {
    status = 'blocked';
    statusColor = 'bg-red-500/10 text-red-500 border-red-500/20';
  } else if (usagePercent >= 90) {
    status = 'critical';
    statusColor = 'bg-red-500/10 text-red-500 border-red-500/20';
  } else if (usagePercent >= 75) {
    status = 'warning';
    statusColor = 'bg-amber-500/10 text-amber-500 border-amber-500/20';
  }
  
  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-1.5 rounded-lg border",
      statusColor
    )}>
      {status === 'blocked' && <AlertTriangle className="h-4 w-4" />}
      <div className="text-xs font-medium">
        {hasOverdue ? (
          <span>BLOCKED - Overdue Invoices</span>
        ) : (
          <span>Credit: €{availableCredit.toFixed(0)} / €{creditLimit.toFixed(0)}</span>
        )}
      </div>
    </div>
  );
};

// Mobile Navigation
const MobileNav = ({ isOpen, onClose, partner, onLogout }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="fixed inset-y-0 left-0 w-72 bg-neutral-900 border-r border-neutral-800">
        <div className="flex items-center justify-between p-4 border-b border-neutral-800">
          <span className="text-lg font-semibold text-white">Menu</span>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <div className="p-4 border-b border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-neutral-800 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-neutral-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">{partner?.name}</p>
              <p className="text-xs text-neutral-500">{partner?.type}</p>
            </div>
          </div>
        </div>
        
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-2 text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors"
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-800">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={onLogout}
          >
            <LogOut className="h-5 w-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Layout Component
export const PartnerLayout = ({ children }) => {
  const { partner, logout } = usePartnerAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [creditData, setCreditData] = useState({
    balance: 0,
    creditLimit: 5000,
    hasOverdue: false
  });
  
  // Fetch credit status
  useEffect(() => {
    const fetchCreditStatus = async () => {
      try {
        const data = await partnerService.getBalance();
        setCreditData({
          balance: data.balance || 0,
          creditLimit: data.credit_limit || 5000,
          hasOverdue: data.has_overdue_invoices || false
        });
      } catch (err) {
        console.error('Failed to fetch credit status:', err);
      }
    };
    
    fetchCreditStatus();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchCreditStatus, 60000);
    return () => clearInterval(interval);
  }, []);
  
  const handleLogout = () => {
    logout();
    navigate('/partner/login');
  };
  
  return (
    <div className="min-h-screen bg-black">
      <Seo {...seo.partner} />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 h-16 bg-neutral-900/80 backdrop-blur-xl border-b border-neutral-800">
        <div className="h-full px-4 lg:px-6 flex items-center justify-between">
          {/* Left: Logo & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            <Link to="/partner/dashboard" className="flex items-center gap-3">
              <span className="font-heading text-lg font-bold text-white tracking-tight">
                BLACK MATTE
              </span>
              <Badge variant="outline" className="text-[10px] text-neutral-500 border-neutral-700">
                PARTNER
              </Badge>
            </Link>
          </div>
          
          {/* Center: Navigation (Desktop) */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/partner/dashboard' && location.pathname.startsWith(item.href));
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                    isActive 
                      ? "text-white bg-neutral-800" 
                      : "text-neutral-400 hover:text-white hover:bg-neutral-800/50"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
          
          {/* Right: Credit Status & Profile */}
          <div className="flex items-center gap-4">
            <CreditStatusBadge 
              balance={creditData.balance}
              creditLimit={creditData.creditLimit}
              hasOverdue={creditData.hasOverdue}
            />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-neutral-800 flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-neutral-400" />
                  </div>
                  <ChevronDown className="h-4 w-4 text-neutral-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-neutral-900 border-neutral-800">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white">{partner?.name}</p>
                  <p className="text-xs text-neutral-500">{partner?.contact_email}</p>
                </div>
                <DropdownMenuSeparator className="bg-neutral-800" />
                <DropdownMenuItem 
                  className="text-red-400 focus:text-red-300 focus:bg-red-500/10 cursor-pointer"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      
      {/* Mobile Navigation */}
      <MobileNav 
        isOpen={mobileNavOpen} 
        onClose={() => setMobileNavOpen(false)}
        partner={partner}
        onLogout={handleLogout}
      />
      
      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <div className="p-4 lg:p-6">
          {/* Credit Warning Banner */}
          {creditData.hasOverdue && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Account Blocked</p>
                <p className="text-xs text-red-400/80">
                  You have overdue invoices. Please make a payment to continue booking.
                </p>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="ml-auto border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => navigate('/partner/invoices')}
              >
                View Invoices
              </Button>
            </div>
          )}
          
          {children}
        </div>
      </main>
    </div>
  );
};

export default PartnerLayout;
